// © 2026 AttainIP Engineering
//
// Pure tooltip positioning — shared by the global TooltipHost and PortalTooltip
// so every tooltip obeys the same rule: stay within the viewport. Given the
// trigger's rect, the measured tooltip size, and the viewport, it flips
// above/below by available space and clamps horizontally so the pill never
// crosses a viewport edge. No DOM access → unit-testable.

export interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipPosition {
  /** viewport (position: fixed) coords for the tooltip's top-left corner */
  top: number;
  left: number;
  placement: TooltipPlacement;
}

/** Gap between the trigger and the pill. */
export const TOOLTIP_GAP = 6;
/** Minimum distance the pill keeps from every viewport edge. */
export const TOOLTIP_MARGIN = 8;

export function positionTooltip(
  target: Box,
  tip: { width: number; height: number },
  viewport: { width: number; height: number },
  preferred: TooltipPlacement = 'top',
): TooltipPosition {
  const spaceAbove = target.top;
  const spaceBelow = viewport.height - (target.top + target.height);
  const needed = tip.height + TOOLTIP_GAP + TOOLTIP_MARGIN;

  // ── Side placements ──────────────────────────────────────────────────────
  // For a control in a narrow margin rail, above/below lands the pill on the
  // very thing the control belongs to. Beside it is the only direction with
  // room, so 'left'/'right' place the pill horizontally, flip to the other
  // side when their own runs out, and centre-then-clamp vertically — the same
  // shape as the vertical case with the axes swapped.
  if (preferred === 'left' || preferred === 'right') {
    const spaceLeft = target.left;
    const spaceRight = viewport.width - (target.left + target.width);
    const needsX = tip.width + TOOLTIP_GAP + TOOLTIP_MARGIN;
    let sidePlacement = preferred;
    if (preferred === 'right' && spaceRight < needsX && spaceLeft > spaceRight) {
      sidePlacement = 'left';
    } else if (preferred === 'left' && spaceLeft < needsX && spaceRight > spaceLeft) {
      sidePlacement = 'right';
    }
    const rawLeft =
      sidePlacement === 'left'
        ? target.left - tip.width - TOOLTIP_GAP
        : target.left + target.width + TOOLTIP_GAP;
    const maxLeftX = viewport.width - tip.width - TOOLTIP_MARGIN;
    const sideLeft = Math.max(
      TOOLTIP_MARGIN,
      Math.min(rawLeft, Math.max(TOOLTIP_MARGIN, maxLeftX)),
    );
    const centreY = target.top + target.height / 2;
    const maxTopY = viewport.height - tip.height - TOOLTIP_MARGIN;
    const sideTop = Math.max(
      TOOLTIP_MARGIN,
      Math.min(centreY - tip.height / 2, Math.max(TOOLTIP_MARGIN, maxTopY)),
    );
    return { top: sideTop, left: sideLeft, placement: sidePlacement };
  }

  // Flip to the side with more room only when the preferred side can't fit.
  let placement: TooltipPlacement = preferred;
  if (preferred === 'top' && spaceAbove < needed && spaceBelow > spaceAbove) {
    placement = 'bottom';
  } else if (preferred === 'bottom' && spaceBelow < needed && spaceAbove > spaceBelow) {
    placement = 'top';
  }

  // Flip chooses the better side; clamp still keeps the pill on-screen when even
  // that side is too short for a tall (e.g. wrapped, multi-line) pill, so it can
  // never cross the top/bottom edge — mirroring the horizontal clamp below.
  const rawTop =
    placement === 'top'
      ? target.top - tip.height - TOOLTIP_GAP
      : target.top + target.height + TOOLTIP_GAP;
  const maxTop = viewport.height - tip.height - TOOLTIP_MARGIN;
  const top = Math.max(TOOLTIP_MARGIN, Math.min(rawTop, Math.max(TOOLTIP_MARGIN, maxTop)));

  // Centre over the trigger, then clamp within [margin, viewport - width - margin].
  const centreX = target.left + target.width / 2;
  const maxLeft = viewport.width - tip.width - TOOLTIP_MARGIN;
  const left = Math.max(TOOLTIP_MARGIN, Math.min(centreX - tip.width / 2, Math.max(TOOLTIP_MARGIN, maxLeft)));

  return { top, left, placement };
}
